import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';

import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Table from '@mui/material/Table';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import { connect } from 'react-redux';

import { useFetchCampaignSubmissionByID } from '@src/hooks';
import { VpIcon, VpDialogTitle, VpNoMediaCollected, VpDialogContent } from '@vp-ui/components';
import { useMediaTableContext } from '@src/hooks/contexts';
import VpMediaInfo from '@vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaInfo';
import VpUpdateThumbnailModal from '@vp-ui/widgets/VpMediaTable/VpUpdateThumbnailModal';

import styles from '@vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaSubmissionDetailsDialog.module.less';
import { getMediaChipMap } from '@src/utils';
import VpMediaListBox from '@vp-ui/components/VpMediaListBox/VpMediaListBox';
import FullScreenDialog from '@vp-ui/dialogs/FullScreenDialog/FullScreenDialog';
import VpCollectedInfoBox from '@vp-ui/components/VpCollectedInfoBox/VpCollectedInfoBox';
import VpSubmissionDetailsResponses from '@vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpSubmissionDetailsResponses';
import { mapSubmissionResponses } from '@src/utils/mapSubmissionResponses';
import { getContentTaggingEnabled } from '@src/redux/modules/auth';
import { REVIEWER_NAME_SENTINEL } from '@vp-ui/form-fields/VpFieldMappingInputs/VpFieldMappingInputs';

// styles
const svgStyles = {
  height: '12px',
  width: '12px'
};
const stackStyles = {
  width: '100%'
};
const contentStyles = {
  display: 'flex',
  overflowY: 'auto'
};

// A requested video that is not part of this submission is ignored, so a stale request can never
// select media from another submission.
export const pickInitialMediaId = (mediaList, pendingMediaId) => {
  const pendingMedia = pendingMediaId && mediaList.find((media) => media.mediaId === pendingMediaId);
  const media = pendingMedia || mediaList[0];
  return media?.mediaId || media?.attachmentId;
};

export const VpMediaSubmissionDetailsDialog = ({ open = false, onClose = null }) => {
  const {
    selectedSubmissionId,
    selectedMediaId,
    setSelectedMediaId,
    pendingMediaId,
    updatingThumbnail,
    setUpdatingThumbnail
  } = useMediaTableContext();
  const { data, isLoading } = useFetchCampaignSubmissionByID(selectedSubmissionId);

  const hasMedia = !!(data?.data?.videoContent?.length || data?.data?.attachmentsContent?.length);

  const attachmentContent = data?.data?.attachmentsContent || [];
  const videoContent = data?.data?.videoContent || [];
  const mediaList = [...videoContent, ...attachmentContent];

  const formattedDate = new Date(data?.data?.submissionDate)?.toDateString();

  const firstMedia = mediaList ? mediaList[0] : null;

  // No media chosen (parent row click, or the table rows clearing it as they mount): open the
  // requested video if there is one, otherwise the first. Re-runs when the selection is cleared,
  // since those rows may mount after this dialog already picked one.
  useEffect(() => {
    if (!selectedMediaId) {
      setSelectedMediaId(pickInitialMediaId(mediaList, pendingMediaId));
    }
  }, [firstMedia, selectedMediaId, pendingMediaId]);

  const responsesList = data?.data?.responses || [];
  const responses = mapSubmissionResponses(responsesList);

  const mediaLabels = useMemo(() => getMediaChipMap(responses), [responses]);

  const titleElementId = data?.data?.campaignTitleElementId;
  const descriptionElementId = data?.data?.campaignDescriptionElementId;
  const submitterName = data?.data?.submitterName;

  const resolveFieldValue = (elementId) => {
    if (!elementId) return null;
    if (elementId === REVIEWER_NAME_SENTINEL) return submitterName || null;
    return responsesList.find((r) => r.campaignElement?.id === elementId)?.answer || null;
  };

  const resolvedTitle = resolveFieldValue(titleElementId);
  const resolvedDescription = resolveFieldValue(descriptionElementId);

  // Guarded because videos have no attachmentId: with nothing selected, `undefined === undefined`
  // would quietly select the first video before the dialog has picked one.
  const rawSelectedMedia = selectedMediaId
    ? mediaList.find((media) => media.mediaId === selectedMediaId || media.attachmentId === selectedMediaId)
    : undefined;
  const selectedMedia = rawSelectedMedia
    ? { ...rawSelectedMedia, resolvedTitle, resolvedDescription }
    : rawSelectedMedia;

  const centerContent = (
    <Box className={styles.vpHeaderDetailsStyle}>
      <VpIcon icon="Send" sx={svgStyles} />
      <Typography variant="span">{data?.data?.campaignName}</Typography>
      <Typography variant="span">●</Typography>
      <VpIcon icon="Calendar" sx={svgStyles} />
      <Typography variant="span">{formattedDate}</Typography>
    </Box>
  );

  const handleClick = (media) => {
    const newMediaId = media?.mediaId || media?.attachmentId;
    setSelectedMediaId(newMediaId);
  };

  return (
    <FullScreenDialog fullWidth onClose={() => onClose(false)} open={open}>
      <VpDialogTitle
        onClose={() => onClose(false)}
        leftContent={`Responses ${data?.data.submitterName ? `/ ${data?.data.submitterName}` : ''}`}
        centerContent={centerContent}
      />
      <VpDialogContent sx={contentStyles} disablePadding>
        <Stack
          justifyContent="flex-start"
          divider={<Divider orientation="vertical" flexItem />}
          direction={{ xs: 'column-reverse', sm: 'row' }}
          sx={stackStyles}
        >
          <Box className={styles.vpDisplayDialogDetailsSection}>
            <Box className={styles.vpDetailsDisplayContextSections}>
              {isLoading ? (
                <Skeleton className={styles.vpMediaDetailsSkeletonList} variant="rectangular" />
              ) : hasMedia ? (
                <VpMediaListBox
                  mediaList={mediaList}
                  onClick={handleClick}
                  selectedMediaId={selectedMediaId}
                  mediaLabels={mediaLabels}
                />
              ) : (
                <VpNoMediaCollected />
              )}
            </Box>
            <Box className={styles.vpDetailsDisplayContextSections}>
              <VpCollectedInfoBox icon="Layout" value="Responses" amount={responses.length || 0} />
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Prompts</TableCell>
                    <TableCell>Answers</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading && (
                    <TableRow>
                      <TableCell>Prompts</TableCell>
                      <TableCell>Answers</TableCell>
                    </TableRow>
                  )}
                  <VpSubmissionDetailsResponses responses={responses} mediaLabels={mediaLabels} campaignId={data?.data?.campaignId}/>
                </TableBody>
              </Table>
            </Box>
          </Box>
          {hasMedia && <VpMediaInfo media={selectedMedia} videos={videoContent} campaignLink={data?.data?.campaignLink} />}
        </Stack>
      </VpDialogContent>
      <VpUpdateThumbnailModal
        selectedMedia={selectedMedia}
        updatingThumbnail={updatingThumbnail}
        setUpdatingThumbnail={setUpdatingThumbnail}
      />
    </FullScreenDialog>
  );
};

VpMediaSubmissionDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  hasMedia: PropTypes.bool,
  contentTaggingEnabled: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  contentTaggingEnabled: getContentTaggingEnabled(state)
});

const mapDispatchToProps = {};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(VpMediaSubmissionDetailsDialog));



// WEBPACK FOOTER //
// ./src/vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaSubmissionDetailsDialog.jsx